/**
 * TrackingScripts
 *
 * Loads all enabled tracking scripts from platform_settings (category: tracking)
 * and injects them into <head> at runtime. Runs once on mount.
 *
 * Scripts are stored as JSON: { enabled: boolean, html: string }
 * The `html` field accepts full <script> blocks, inline JS, or <noscript> tags.
 *
 * Deduplication: each script is stamped with data-lwd-tracking-key so it is
 * never injected twice (guards against React StrictMode double-invoke and
 * any future re-mount scenarios).
 *
 * Admin-managed via Settings → Tracking & Analytics in the admin dashboard.
 */
import { useEffect } from 'react';
import { supabase }  from '../lib/supabaseClient';

/**
 * Parse an HTML string and inject all <script> nodes into <head>,
 * and all <noscript>/<img> pixel nodes into <body>.
 * Creates real script elements so async/src scripts execute correctly.
 */
function injectHtml(html, trackingKey) {
  // Guard: already injected in this session
  if (document.querySelector(`[data-lwd-tracking-key="${trackingKey}"]`)) return;

  const wrap = document.createElement('div');
  wrap.innerHTML = html;

  // Verification/meta-only blocks: if the same meta tag(s) already exist in
  // <head> (e.g. also kept static in index.html so non-JS verifiers and the
  // coming-soon overlay see them), skip, don't inject a duplicate.
  const metaOnly = wrap.querySelector('meta[name]') && !wrap.querySelector('script, noscript');
  if (metaOnly) {
    const names = Array.from(wrap.querySelectorAll('meta[name]')).map(m => m.getAttribute('name'));
    if (names.length && names.every(n => document.head.querySelector(`meta[name="${n}"]`))) return;
  }

  let injected = false;

  wrap.querySelectorAll('script').forEach((src) => {
    const s = document.createElement('script');
    // Copy all attributes
    Array.from(src.attributes).forEach(a => s.setAttribute(a.name, a.value));
    // Copy inline content
    if (src.textContent?.trim()) s.textContent = src.textContent;
    // Stamp for dedup
    s.setAttribute('data-lwd-tracking-key', trackingKey);
    s.setAttribute('data-lwd-tracking', 'true');
    document.head.appendChild(s);
    injected = true;
  });

  // Pixel / noscript tags go to body
  wrap.querySelectorAll('noscript, img[src*="track"]').forEach((el) => {
    const clone = el.cloneNode(true);
    clone.setAttribute('data-lwd-tracking-key', trackingKey);
    document.body.appendChild(clone);
    injected = true;
  });

  // Fallback: if no <script> or <noscript> found, try contextualFragment
  if (!injected && html.trim()) {
    try {
      const range = document.createRange();
      range.selectNode(document.head);
      const frag = range.createContextualFragment(html);
      // Stamp the first element if possible
      const first = frag.firstElementChild;
      if (first) first.setAttribute('data-lwd-tracking-key', trackingKey);
      document.head.appendChild(frag);
    } catch { /* fail silently */ }
  }
}

export default function TrackingScripts() {
  useEffect(() => {
    supabase
      .from('platform_settings')
      .select('key,value')
      .eq('category', 'tracking')
      .then(({ data }) => {
        if (!data?.length) return;

        data.forEach(({ key, value }) => {
          try {
            const parsed = typeof value === 'string' ? JSON.parse(value) : value;
            const { enabled, html } = parsed || {};
            if (!enabled || !html?.trim()) return;
            injectHtml(html, key);
            console.log(`[TrackingScripts] Injected ${key}`);
          } catch (e) {
            console.error(`[TrackingScripts] Failed to parse ${key}:`, e.message);
          }
        });
      })
      .catch((e) => {
        console.error('[TrackingScripts] Database query failed:', e.message);
      });
  }, []);

  return null;
}
